/**
 * Common API Types
 * Pagination, error responses, and shared types
 */

// =============================================================================
// PAGINATION
// =============================================================================

/**
 * Pagination metadata for Document endpoints
 */
export interface DocumentPaginationDto {
    total: number;
    limit: number;
    offset: number;
    returned: number;
}

/**
 * Pagination metadata for Files, Plans, Entitlements endpoints
 */
export interface CommonPaginationDto {
    total: number;
    pageSize: number;
    skip: number;
    returned: number;
}

/**
 * Generic paginated response with Document-style pagination
 */
export interface PaginatedResponse<T> {
    data: T[];
    pagination: DocumentPaginationDto;
}

/**
 * Generic paginated response with Common-style pagination
 */
export interface PaginatedResponseCommon<T> {
    data: T[];
    pagination: CommonPaginationDto;
}

/**
 * List response without pagination
 */
export interface ListResponse<T> {
    data: T[];
    total: number;
}

// =============================================================================
// ERROR HANDLING
// =============================================================================

/**
 * Standard error response format from the API
 *
 * @property error - High-level error category (e.g., "NotFound", "Validation", "Authentication")
 * @property message - Human-readable error message (only populated in development mode for security)
 * @property entityId - ID of the related entity. Frontend can use this to create links to the affected resource
 * @property entityType - Type of the related entity. Frontend can use this to create links
 * @property code - Specific exception code (e.g., "NotFoundDocument"). Use for i18n message lookup
 * @property numericCode - Numeric exception code for programmatic handling
 * @property exceptionId - Database ID of the recorded exception. Include in support tickets for debugging
 *
 * Additional fields for specific error types:
 * - QuotaExceeded errors include: remainingQuota, requestedPages, currentUsage, quotaLimit
 */
export interface ErrorResponseDto {
    /** High-level error category */
    error: string;
    /** Human-readable error message (dev mode only) */
    message?: string;
    /** ID of the related entity for linking */
    entityId?: number;
    /** Type of the related entity (see EntityTypeEnum) */
    entityType?: string;
    /** Specific exception code for i18n lookup */
    code?: string;
    /** Numeric exception code for programmatic handling */
    numericCode?: number;
    /** Database ID of the recorded exception for support tickets */
    exceptionId?: number;
    /** Remaining page quota (for BizQuotaExceeded errors) */
    remainingQuota?: number;
    /** Number of pages requested in the upload (for BizQuotaExceeded errors) */
    requestedPages?: number;
    /** Current page usage for the month (for BizQuotaExceeded errors) */
    currentUsage?: number;
    /** Total page quota limit (for BizQuotaExceeded errors) */
    quotaLimit?: number;
}

/**
 * High-level error categories used in error responses.
 * Each category maps to a specific HTTP status code.
 */
export enum ErrorCategoryEnum {
    /** 401 - Authentication failures (token expired, invalid, or missing) */
    Authentication = "Authentication",
    /** 403 - Authorization failures (access denied, wrong tenant, insufficient permissions) */
    Authorization = "Authorization",
    /** 400 - Input validation failures (missing fields, invalid format, constraints violated) */
    Validation = "Validation",
    /** 404 - Requested resource does not exist */
    NotFound = "NotFound",
    /** 409 - Business rule violations (duplicates, invalid state transitions, tolerance exceeded) */
    Business = "Business",
    /** 500 - Pipeline processing failures (OCR, classification, parsing, matching) */
    Processing = "Processing",
    /** 502 - External service failures (OpenAI, blob storage, HTTP requests) */
    Integration = "Integration",
    /** 500 - System-level failures (database, queue, configuration) */
    Infrastructure = "Infrastructure",
}

/**
 * Specific exception codes indicating what went wrong.
 * Use these codes for:
 * - Frontend i18n: Map codes to localized user messages
 * - Programmatic handling: Use numeric ranges to categorize errors
 * - Debugging: Include in logs and support tickets
 *
 * Numeric Ranges:
 * - 1xx: Authentication - Token and credential issues
 * - 2xx: Authorization - Permission and access issues
 * - 3xx: Validation - Input validation failures
 * - 4xx: Not Found - Missing resources
 * - 5xx: Business - Business rule violations
 * - 6xx: Processing - Pipeline step failures
 * - 7xx: Integration - External service failures
 * - 8xx: Infrastructure - System-level failures
 * - 9xx: Unknown - Unclassified errors
 */
export enum ExceptionCodeEnum {
    // ==========================================================================
    // Authentication Codes (1xx)
    // ==========================================================================
    /** 100 - JWT token has expired */
    AuthTokenExpired = "AuthTokenExpired",
    /** 101 - JWT token is invalid or malformed */
    AuthTokenInvalid = "AuthTokenInvalid",
    /** 102 - Required claims missing from token */
    AuthMissingClaims = "AuthMissingClaims",
    /** 103 - Token signature verification failed */
    AuthSignatureInvalid = "AuthSignatureInvalid",
    /** 104 - Authorization header/API key missing or malformed */
    AuthMissingCredentials = "AuthMissingCredentials",

    // ==========================================================================
    // Authorization Codes (2xx)
    // ==========================================================================
    /** 200 - User does not have permission */
    AuthzAccessDenied = "AuthzAccessDenied",
    /** 201 - Resource belongs to different tenant */
    AuthzTenantMismatch = "AuthzTenantMismatch",
    /** 202 - Resource belongs to different organization */
    AuthzOrganizationMismatch = "AuthzOrganizationMismatch",
    /** 203 - User role does not permit operation */
    AuthzInsufficientRole = "AuthzInsufficientRole",
    /** 204 - Feature not included in plan */
    AuthzEntitlementDenied = "AuthzEntitlementDenied",
    /** 205 - Authenticated user is not assigned to any organization */
    AuthzNoOrganizationAssigned = "AuthzNoOrganizationAssigned",
    /** 206 - Client could not be resolved for the authenticated principal */
    AuthzClientUnresolved = "AuthzClientUnresolved",

    // ==========================================================================
    // Validation Codes (3xx)
    // ==========================================================================
    /** 300 - Required field is missing or empty */
    ValMissingRequiredField = "ValMissingRequiredField",
    /** 301 - Field value has invalid format */
    ValInvalidFormat = "ValInvalidFormat",
    /** 302 - Field value exceeds maximum length */
    ValMaxLengthExceeded = "ValMaxLengthExceeded",
    /** 303 - Field value is below minimum */
    ValBelowMinimum = "ValBelowMinimum",
    /** 304 - Field value is above maximum */
    ValAboveMaximum = "ValAboveMaximum",
    /** 305 - File size exceeds limit */
    ValFileTooLarge = "ValFileTooLarge",
    /** 306 - File type is not supported */
    ValUnsupportedFileType = "ValUnsupportedFileType",
    /** 307 - Invalid ID format */
    ValInvalidId = "ValInvalidId",
    /** 308 - Invalid date/time format */
    ValInvalidDateTime = "ValInvalidDateTime",
    /** 309 - Invalid enum value */
    ValInvalidEnumValue = "ValInvalidEnumValue",
    /** 310 - Invalid organization name format */
    ValInvalidOrganizationFormat = "ValInvalidOrganizationFormat",
    /** 314 - File type is not enabled for the target organization */
    ValFileTypeNotEnabled = "ValFileTypeNotEnabled",

    // ==========================================================================
    // Not Found Codes (4xx)
    // ==========================================================================
    /** 400 - Document not found */
    NotFoundDocument = "NotFoundDocument",
    /** 401 - File not found */
    NotFoundFile = "NotFoundFile",
    /** 402 - Job not found */
    NotFoundJob = "NotFoundJob",
    /** 403 - User not found */
    NotFoundUser = "NotFoundUser",
    /** 404 - Organization not found */
    NotFoundOrganization = "NotFoundOrganization",
    /** 405 - Client not found */
    NotFoundClient = "NotFoundClient",
    /** 406 - Document type not found */
    NotFoundDocumentType = "NotFoundDocumentType",
    /** 407 - Rule not found */
    NotFoundRule = "NotFoundRule",
    /** 408 - Workflow not found */
    NotFoundWorkflow = "NotFoundWorkflow",
    /** 409 - Custom field definition not found */
    NotFoundCustomField = "NotFoundCustomField",
    /** 410 - Blob content not found in storage */
    NotFoundBlobContent = "NotFoundBlobContent",
    /** 411 - Exception record not found */
    NotFoundException = "NotFoundException",
    /** 412 - Prompt template not found */
    NotFoundPromptTemplate = "NotFoundPromptTemplate",
    /** 413 - Entitlement not found */
    NotFoundEntitlement = "NotFoundEntitlement",

    // ==========================================================================
    // Business Logic Codes (5xx)
    // ==========================================================================
    /** 500 - Duplicate file detected (same checksum) */
    BizDuplicateFile = "BizDuplicateFile",
    /** 501 - Duplicate document detected */
    BizDuplicateDocument = "BizDuplicateDocument",
    /** 502 - Values exceed tolerance limits */
    BizToleranceExceeded = "BizToleranceExceeded",
    /** 503 - Export blocked due to unresolved exceptions */
    BizExportBlocked = "BizExportBlocked",
    /** 504 - Invalid status change */
    BizInvalidStateTransition = "BizInvalidStateTransition",
    /** 505 - Resolution note required */
    BizResolutionNoteRequired = "BizResolutionNoteRequired",
    /** 506 - Mapping could not be completed */
    BizMappingFailure = "BizMappingFailure",
    /** 507 - No matching records found */
    BizMatchFailure = "BizMatchFailure",
    /** 508 - Entity is in deleted state */
    BizEntityDeleted = "BizEntityDeleted",
    /** 509 - Maximum retry attempts exceeded */
    BizMaxRetriesExceeded = "BizMaxRetriesExceeded",
    /** 510 - Page or document quota exceeded */
    BizQuotaExceeded = "BizQuotaExceeded",
    /** 511 - No vendor match found during vendor lookup */
    BizVendorNotFound = "BizVendorNotFound",
    /** 513 - Custom field value changed by another user since it was loaded */
    BizConcurrentUpdateConflict = "BizConcurrentUpdateConflict",

    // ==========================================================================
    // Processing Codes (6xx)
    // ==========================================================================
    /** 600 - OCR processing failed */
    ProcOcrFailed = "ProcOcrFailed",
    /** 601 - OCR returned empty/no text */
    ProcOcrEmptyResult = "ProcOcrEmptyResult",
    /** 602 - Classification failed */
    ProcClassificationFailed = "ProcClassificationFailed",
    /** 603 - Classification returned no documents */
    ProcClassificationEmptyResult = "ProcClassificationEmptyResult",
    /** 604 - Document type was not recognized during classification */
    ProcClassificationUnknownType = "ProcClassificationUnknownType",
    /** 605 - Classification page range validation failed after retries */
    ProcClassificationValidationFailed = "ProcClassificationValidationFailed",
    /** 606 - Parse/extraction failed */
    ProcParseFailed = "ProcParseFailed",
    /** 607 - Parse returned no fields */
    ProcParseEmptyResult = "ProcParseEmptyResult",
    /** 608 - Match processing failed */
    ProcMatchFailed = "ProcMatchFailed",
    /** 609 - Prerequisite step not completed */
    ProcPrerequisiteNotMet = "ProcPrerequisiteNotMet",
    /** 610 - Unsupported job type */
    ProcUnsupportedJobType = "ProcUnsupportedJobType",
    /** 611 - Failed to deserialize job message */
    ProcJobDeserializationFailed = "ProcJobDeserializationFailed",
    /** 612 - Failed to update entity status */
    ProcStatusUpdateFailed = "ProcStatusUpdateFailed",
    /** 613 - Extraction confidence is low */
    ProcExtractionUncertain = "ProcExtractionUncertain",
    /** 614 - Failed to create documents */
    ProcDocumentCreationFailed = "ProcDocumentCreationFailed",
    /** 615 - Failed to check quota */
    ProcQuotaCheckFailed = "ProcQuotaCheckFailed",
    /** 616 - Failed to track usage */
    ProcUsageTrackingFailed = "ProcUsageTrackingFailed",
    /** 617 - Calculated field expression evaluation failed */
    ProcCalculatedFieldFailed = "ProcCalculatedFieldFailed",

    // ==========================================================================
    // Integration Codes (7xx)
    // ==========================================================================
    /** 700 - OpenAI API call failed */
    IntOpenAiFailed = "IntOpenAiFailed",
    /** 701 - OpenAI returned invalid response */
    IntOpenAiInvalidResponse = "IntOpenAiInvalidResponse",
    /** 702 - AirTable API call failed */
    IntAirTableFailed = "IntAirTableFailed",
    /** 703 - Azure Blob Storage operation failed */
    IntBlobStorageFailed = "IntBlobStorageFailed",
    /** 704 - Vercel Blob Storage operation failed */
    IntVercelBlobFailed = "IntVercelBlobFailed",
    /** 705 - External HTTP request failed */
    IntHttpRequestFailed = "IntHttpRequestFailed",
    /** 706 - Rate limit exceeded */
    IntRateLimited = "IntRateLimited",
    /** 707 - External service timeout */
    IntTimeout = "IntTimeout",
    /** 708 - External service returned error */
    IntServiceError = "IntServiceError",
    /** 709 - Clerk API call failed */
    IntClerkFailed = "IntClerkFailed",
    /** 710 - Clerk metadata synchronization failed */
    IntClerkSyncFailed = "IntClerkSyncFailed",

    // ==========================================================================
    // Infrastructure Codes (8xx)
    // ==========================================================================
    /** 800 - Database operation failed */
    InfDatabaseError = "InfDatabaseError",
    /** 801 - Queue operation failed */
    InfQueueError = "InfQueueError",
    /** 802 - Configuration/environment variable missing */
    InfConfigurationMissing = "InfConfigurationMissing",
    /** 803 - Service Bus connection failed */
    InfServiceBusError = "InfServiceBusError",
    /** 804 - Internal service not available */
    InfServiceUnavailable = "InfServiceUnavailable",
    /** 805 - JSON serialization failed */
    InfSerializationError = "InfSerializationError",
    /** 806 - Client settings not configured */
    InfClientSettingsInvalid = "InfClientSettingsInvalid",
    /** 807 - Schema parsing/validation failed */
    InfSchemaInvalid = "InfSchemaInvalid",

    // ==========================================================================
    // Unknown (9xx)
    // ==========================================================================
    /** 999 - Unclassified error */
    Unknown = "Unknown",
}

/**
 * Map of exception code to its numeric value
 */
export const ExceptionCodeNumericMap: Record<ExceptionCodeEnum, number> = {
    // Authentication (1xx)
    [ExceptionCodeEnum.AuthTokenExpired]: 100,
    [ExceptionCodeEnum.AuthTokenInvalid]: 101,
    [ExceptionCodeEnum.AuthMissingClaims]: 102,
    [ExceptionCodeEnum.AuthSignatureInvalid]: 103,
    [ExceptionCodeEnum.AuthMissingCredentials]: 104,
    // Authorization (2xx)
    [ExceptionCodeEnum.AuthzAccessDenied]: 200,
    [ExceptionCodeEnum.AuthzTenantMismatch]: 201,
    [ExceptionCodeEnum.AuthzOrganizationMismatch]: 202,
    [ExceptionCodeEnum.AuthzInsufficientRole]: 203,
    [ExceptionCodeEnum.AuthzEntitlementDenied]: 204,
    [ExceptionCodeEnum.AuthzNoOrganizationAssigned]: 205,
    [ExceptionCodeEnum.AuthzClientUnresolved]: 206,
    // Validation (3xx)
    [ExceptionCodeEnum.ValMissingRequiredField]: 300,
    [ExceptionCodeEnum.ValInvalidFormat]: 301,
    [ExceptionCodeEnum.ValMaxLengthExceeded]: 302,
    [ExceptionCodeEnum.ValBelowMinimum]: 303,
    [ExceptionCodeEnum.ValAboveMaximum]: 304,
    [ExceptionCodeEnum.ValFileTooLarge]: 305,
    [ExceptionCodeEnum.ValUnsupportedFileType]: 306,
    [ExceptionCodeEnum.ValInvalidId]: 307,
    [ExceptionCodeEnum.ValInvalidDateTime]: 308,
    [ExceptionCodeEnum.ValInvalidEnumValue]: 309,
    [ExceptionCodeEnum.ValInvalidOrganizationFormat]: 310,
    [ExceptionCodeEnum.ValFileTypeNotEnabled]: 314,
    // Not Found (4xx)
    [ExceptionCodeEnum.NotFoundDocument]: 400,
    [ExceptionCodeEnum.NotFoundFile]: 401,
    [ExceptionCodeEnum.NotFoundJob]: 402,
    [ExceptionCodeEnum.NotFoundUser]: 403,
    [ExceptionCodeEnum.NotFoundOrganization]: 404,
    [ExceptionCodeEnum.NotFoundClient]: 405,
    [ExceptionCodeEnum.NotFoundDocumentType]: 406,
    [ExceptionCodeEnum.NotFoundRule]: 407,
    [ExceptionCodeEnum.NotFoundWorkflow]: 408,
    [ExceptionCodeEnum.NotFoundCustomField]: 409,
    [ExceptionCodeEnum.NotFoundBlobContent]: 410,
    [ExceptionCodeEnum.NotFoundException]: 411,
    [ExceptionCodeEnum.NotFoundPromptTemplate]: 412,
    [ExceptionCodeEnum.NotFoundEntitlement]: 413,
    // Business (5xx)
    [ExceptionCodeEnum.BizDuplicateFile]: 500,
    [ExceptionCodeEnum.BizDuplicateDocument]: 501,
    [ExceptionCodeEnum.BizToleranceExceeded]: 502,
    [ExceptionCodeEnum.BizExportBlocked]: 503,
    [ExceptionCodeEnum.BizInvalidStateTransition]: 504,
    [ExceptionCodeEnum.BizResolutionNoteRequired]: 505,
    [ExceptionCodeEnum.BizMappingFailure]: 506,
    [ExceptionCodeEnum.BizMatchFailure]: 507,
    [ExceptionCodeEnum.BizEntityDeleted]: 508,
    [ExceptionCodeEnum.BizMaxRetriesExceeded]: 509,
    [ExceptionCodeEnum.BizQuotaExceeded]: 510,
    [ExceptionCodeEnum.BizVendorNotFound]: 511,
    [ExceptionCodeEnum.BizConcurrentUpdateConflict]: 513,
    // Processing (6xx)
    [ExceptionCodeEnum.ProcOcrFailed]: 600,
    [ExceptionCodeEnum.ProcOcrEmptyResult]: 601,
    [ExceptionCodeEnum.ProcClassificationFailed]: 602,
    [ExceptionCodeEnum.ProcClassificationEmptyResult]: 603,
    [ExceptionCodeEnum.ProcClassificationUnknownType]: 604,
    [ExceptionCodeEnum.ProcClassificationValidationFailed]: 605,
    [ExceptionCodeEnum.ProcParseFailed]: 606,
    [ExceptionCodeEnum.ProcParseEmptyResult]: 607,
    [ExceptionCodeEnum.ProcMatchFailed]: 608,
    [ExceptionCodeEnum.ProcPrerequisiteNotMet]: 609,
    [ExceptionCodeEnum.ProcUnsupportedJobType]: 610,
    [ExceptionCodeEnum.ProcJobDeserializationFailed]: 611,
    [ExceptionCodeEnum.ProcStatusUpdateFailed]: 612,
    [ExceptionCodeEnum.ProcExtractionUncertain]: 613,
    [ExceptionCodeEnum.ProcDocumentCreationFailed]: 614,
    [ExceptionCodeEnum.ProcQuotaCheckFailed]: 615,
    [ExceptionCodeEnum.ProcUsageTrackingFailed]: 616,
    [ExceptionCodeEnum.ProcCalculatedFieldFailed]: 617,
    // Integration (7xx)
    [ExceptionCodeEnum.IntOpenAiFailed]: 700,
    [ExceptionCodeEnum.IntOpenAiInvalidResponse]: 701,
    [ExceptionCodeEnum.IntAirTableFailed]: 702,
    [ExceptionCodeEnum.IntBlobStorageFailed]: 703,
    [ExceptionCodeEnum.IntVercelBlobFailed]: 704,
    [ExceptionCodeEnum.IntHttpRequestFailed]: 705,
    [ExceptionCodeEnum.IntRateLimited]: 706,
    [ExceptionCodeEnum.IntTimeout]: 707,
    [ExceptionCodeEnum.IntServiceError]: 708,
    [ExceptionCodeEnum.IntClerkFailed]: 709,
    [ExceptionCodeEnum.IntClerkSyncFailed]: 710,
    // Infrastructure (8xx)
    [ExceptionCodeEnum.InfDatabaseError]: 800,
    [ExceptionCodeEnum.InfQueueError]: 801,
    [ExceptionCodeEnum.InfConfigurationMissing]: 802,
    [ExceptionCodeEnum.InfServiceBusError]: 803,
    [ExceptionCodeEnum.InfServiceUnavailable]: 804,
    [ExceptionCodeEnum.InfSerializationError]: 805,
    [ExceptionCodeEnum.InfClientSettingsInvalid]: 806,
    [ExceptionCodeEnum.InfSchemaInvalid]: 807,
    // Unknown (9xx)
    [ExceptionCodeEnum.Unknown]: 999,
};

/**
 * Get error category from numeric code range
 */
export function getErrorCategoryFromNumericCode(numericCode: number): string {
    if (numericCode >= 100 && numericCode < 200) return "auth";
    if (numericCode >= 200 && numericCode < 300) return "authorization";
    if (numericCode >= 300 && numericCode < 400) return "validation";
    if (numericCode >= 400 && numericCode < 500) return "notFound";
    if (numericCode >= 500 && numericCode < 600) return "business";
    if (numericCode >= 600 && numericCode < 700) return "processing";
    if (numericCode >= 700 && numericCode < 800) return "integration";
    if (numericCode >= 800 && numericCode < 900) return "infrastructure";
    return "unknown";
}

// =============================================================================
// ENTITY TYPES
// =============================================================================

/**
 * Entity types for error responses and custom fields
 */
export enum EntityTypeEnum {
    Document = "Document",
    File = "File",
    Job = "Job",
    LineItem = "LineItem",
    User = "User",
    Organization = "Organization",
    Client = "Client",
}

// =============================================================================
// BILLING
// =============================================================================

/**
 * Billing cycle values for client plans
 */
export enum BillingCycleEnum {
    Monthly = "Monthly",
    Annual = "Annual",
}
